import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Timer, Sparkles, ArrowRight } from 'lucide-react';
import type { Product } from '../types';
import { FeaturedProductCard } from '../components/product/FeaturedProductCard';
import { productService } from '../services/productService';

/**
 * Pagina "Offerte a tempo" — mostra TUTTE le promo attive caricate
 * via ``/api/products/featured``. Stesso endpoint usato dalla homepage
 * "In evidenza", così i prodotti restano consistenti tra le due viste.
 */
function fmtPrice(n: number): string {
  return n.toFixed(2).replace('.', ',');
}

export default function OffertePage() {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    productService.featured(48, true)
      .then(res => { if (!cancelled) setAllProducts(res.items); })
      .catch(() => { if (!cancelled) setAllProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Solo offerte a tempo limitato (non permanenti)
  const products = allProducts.filter(p => p.promo?.isPermanent !== true);

  // Calcola sconto massimo per il claim hero
  const maxDiscount = products.reduce((max, p) => {
    const promo = p.promo;
    if (!promo?.priceOverride || !p.listino10 || p.listino10 <= promo.priceOverride) return max;
    const pct = Math.round((1 - promo.priceOverride / p.listino10) * 100);
    return Math.max(max, pct);
  }, 0);

  return (
    <div className="min-h-screen bg-secondary/20" data-testid="offerte-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1E4D8C] via-[#2B5EA7] to-[#1E4D8C] text-white">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, white 1px, transparent 1px), radial-gradient(circle at 75% 50%, white 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="flex-shrink-0 w-20 h-20 rounded-2xl bg-amber-400 flex items-center justify-center shadow-2xl">
              <Zap className="w-10 h-10 text-[#1E4D8C]" fill="currentColor" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-widest mb-2">
                <Sparkles className="w-4 h-4" />
                Offerte a tempo limitato
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
                {maxDiscount > 0 ? (
                  <>Risparmia fino al <span className="text-amber-300">{maxDiscount}%</span></>
                ) : (
                  <>Le nostre offerte del momento</>
                )}
              </h1>
              <p className="text-white/80 max-w-2xl text-sm sm:text-base">
                Prodotti selezionati a prezzo speciale per pochi giorni. Stock limitato — non lasciarti scappare l'occasione.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground" data-testid="offers-count">{products.length}</span> offerte a tempo
          </div>
          <Link to="/outlet" className="text-sm font-medium text-[#2B5EA7] hover:underline flex items-center gap-1" data-testid="link-to-outlet">
            Vedi anche Outlet <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="offers-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border h-[360px] animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="offers-empty">
            <Timer className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nessuna offerta attiva al momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="offers-grid">
            {products.map(p => {
              const promo = p.promo;
              const priceCurrent = fmtPrice(promo?.priceOverride ?? p.listino10 ?? 0);
              const priceOld = promo?.priceOverride && p.listino10 && p.listino10 > promo.priceOverride
                ? fmtPrice(p.listino10)
                : undefined;
              const stock = (p.stock as number | undefined) ?? 20;
              return (
                <FeaturedProductCard
                  key={p.sku}
                  product={p}
                  tag="OFFERTA"
                  endDate={promo?.endDate ?? undefined}
                  priceCurrent={priceCurrent}
                  priceOld={priceOld}
                  stock={stock}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
